import { createSelector } from '@reduxjs/toolkit';

import FieldType from '@app/store/FieldType';
import MeansType from '@app/store/MeansType';
import FieldInformation, {
  CheckboxFieldInformation,
  CheckboxGroupInformation,
  SignatureFieldInformation,
  TextFieldInformation,
} from '@app/store/template/FieldInformation';
import FieldPosition from '@app/store/template/FieldPosition';
import {
  TemplateAttachment,
  TemplateAttachmentState,
} from '@app/store/template/reducers/attachments';
import { TemplateFieldInformationState } from '@app/store/template/reducers/field/information';
import { TemplateParticipantByIdState } from '@app/store/template/reducers/participant/byId';
import { RootState } from '@app/store/types';

export const templateSelector = (state: RootState) => state.template;
export const getTemplateSignerAttachments = createSelector(
  templateSelector,
  (template) => template.attachments
);
/**
 * 파일첨부 요청 전체 수
 */
export const getAttachmentRequestTotalCount = createSelector(
  getTemplateSignerAttachments,
  (attachments: TemplateAttachmentState) =>
    attachments.reduce(
      (sum: number, attachment: TemplateAttachment) => sum + attachment.requests.length,
      0
    )
);
export const fieldSelector = (state: RootState) => templateSelector(state).field;
export const participantSelector = createSelector(
  templateSelector,
  (template) => template.participant
);
export const idSelector = createSelector(templateSelector, (template) => template.id);
export const isFetchingSelector = createSelector(
  templateSelector,
  (template) => template.isFetching
);
export const fileIdSelector = createSelector(templateSelector, (template) => template.fileId);
export const originTitleSelector = createSelector(
  templateSelector,
  (template) => template.originTitle
);
export const titleSelector = createSelector(templateSelector, (template) => template.title || '');
export const urlSelector = createSelector(templateSelector, (template) => template.url);

export const labelIdsSelector = createSelector(templateSelector, (template) => template.labelIds);

// TODO: @Juyeong-Byeon getPdfArrayBuffer selector와 관련 slice 제거
export const getPdfArrayBuffer = createSelector(
  templateSelector,
  (template) => template.pdfArrayBuffer
);

export const getHasTemplateId = createSelector(idSelector, (id) => !!id);

export const fieldIdsSelector = createSelector(fieldSelector, (field) => field.ids);
export const idsInPagesSelector = createSelector(fieldSelector, (field) => field.idsInPages);
export const informationSelector = (state: RootState): TemplateFieldInformationState =>
  fieldSelector(state).information;
export const isInsertingSelector = createSelector(fieldSelector, (field) => field.isInserting);
export const latestIdsSelector = createSelector(fieldSelector, (field) => field.latestIds);
export const positionsSelector = createSelector(fieldSelector, (field) => field.positions);
export const sizesSelector = createSelector(fieldSelector, (field) => field.sizes);

export const getSizeById = (state: RootState, props: { fieldId: string }) =>
  sizesSelector(state)[props.fieldId];

export const targetSelector = createSelector(fieldSelector, (field) => field.target);

export const getPropsParticipantId = (_: RootState, props: { participantId: string }) =>
  props.participantId;

export const participantFieldSelector = createSelector(
  participantSelector,
  (participant) => participant.field
);

export const participantByIdSelector = createSelector(
  participantSelector,
  (participant) => participant.byId
);

export const participantIdsSelector = createSelector(
  participantSelector,
  (participant) => participant.ids
);

/**
 * 특정 서명자의 정보를 가져온다.
 */
export const getParticipant = createSelector(
  participantByIdSelector,
  getPropsParticipantId,
  (byId, participantId) => byId[participantId]
);

/**
 * 특정 서명자의 이름을 가져온다.
 */
export const getParticipantName = createSelector(getParticipant, (participant) => participant.name);

/**
 * 특정 서명자의 역할을 가져온다.
 */
export const getParticipantRole = createSelector(getParticipant, (participant) => participant.role);

/**
 * 템플릿에서 특정 서명자 이름이 있으면 이름을, 아니면 역할을 반환한다.
 */
export const getParticipantNameOrRole = createSelector(
  getParticipantName,
  getParticipantRole,
  (name, role) => {
    return !name || name.length === 0 ? role : name;
  }
);

export const getIsTemplateSignInOrder = (state: RootState): boolean =>
  participantSelector(state).signingProgressType === 'inOrder';

export const getParticipantInformation = createSelector(
  participantByIdSelector,
  participantIdsSelector,
  (
    byId: TemplateParticipantByIdState,
    ids: string[]
  ): Array<{
    id: string;
    means: string;
    meansType: MeansType;
    name: string;
    role: string;
    predefined?: boolean;
  }> => {
    // 서명자 정보는 ids 배열 순서대로 정렬해야합니다.
    return ids.map(
      (
        participantId
      ): {
        id: string;
        means: string;
        meansType: MeansType;
        name: string;
        role: string;
        predefined?: boolean;
      } => {
        const participant = byId[participantId];

        return {
          id: participant.id,
          means: participant.means,
          meansType: participant.meansType,
          name: participant.name,
          role: participant.role,
          predefined: participant.predefined,
        };
      }
    );
  }
);

export const getTrimmedFieldDataLabelMap = createSelector(
  fieldIdsSelector,
  informationSelector,
  (ids, informations) => {
    const dataLabelMap: Record<string, string> = {};

    ids.forEach((id) => {
      dataLabelMap[id] = informations[id].dataLabel.trim();
    });

    return dataLabelMap;
  }
);

type CheckboxFieldData = CheckboxFieldInformation & FieldPosition;
type CheckboxGroupData = CheckboxGroupInformation & FieldPosition;
type SignatureFieldData = SignatureFieldInformation & FieldPosition;
type TextFieldData = TextFieldInformation & FieldPosition;

type FieldData = CheckboxFieldData | CheckboxGroupData | SignatureFieldData | TextFieldData;

export const getFields = createSelector(
  [fieldIdsSelector, informationSelector, positionsSelector],
  (
    fieldIds,
    information,
    positions
  ): {
    [id: string]: FieldData;
  } => {
    const fieldData: FieldData[] = fieldIds
      .map((fieldId) => {
        const informationValue = (information[fieldId] ?? null) as FieldInformation | null;
        const positionsValue: FieldPosition | null = positions[fieldId] ?? null;
        if (informationValue && positionsValue) {
          return {
            ...informationValue,
            ...positionsValue,
          };
        }
        return null;
      })
      .filter((f): f is FieldData => f !== null);

    return Object.fromEntries(
      fieldData.map((field: FieldInformation & FieldPosition) => [field.id, field])
    );
  }
);

export const getParticipantsWithFieldData = createSelector(
  [participantIdsSelector, participantByIdSelector, participantFieldSelector, getFields],
  (
    participantIds: string[],
    participantById: TemplateParticipantByIdState,
    participantField,
    fields
  ) =>
    participantIds.map((participantId) => {
      const byIdValue = participantById[participantId];
      const fieldIdsInParticipant = participantField[participantId] || [];
      const fieldInParticipant = fieldIdsInParticipant.map((inputId, index) => ({
        ...fields[inputId],
        index,
      }));
      return { ...byIdValue, participantId, fields: fieldInParticipant || null };
    })
);

export const getPageNumber = (_: RootState, props: { pageNumber: number }) => props.pageNumber;

export const getFieldIdsInPage = createSelector(
  [idsInPagesSelector, getPageNumber],
  (idsInPages, pageNumber) => {
    if (pageNumber in idsInPages) {
      return idsInPages[pageNumber];
    }
    return null;
  }
);

export const makeGetPageFields = createSelector(
  getFieldIdsInPage,
  informationSelector,
  participantIdsSelector,
  participantFieldSelector,
  positionsSelector,
  sizesSelector,
  (
    fieldIdsInPage,
    information,
    participantsIds: string[],
    participantFieldIds,
    positions,
    sizes
  ) => {
    if (!fieldIdsInPage) {
      return null;
    }

    return fieldIdsInPage.map((fieldId) => {
      const currentFieldInformation = information[fieldId];
      const fieldParticipantId = information[fieldId].participantId;
      const participantFieldList = participantFieldIds[fieldParticipantId];
      const participantFieldIndex = participantFieldList
        .filter((id) => currentFieldInformation.type === information[id].type)
        .indexOf(fieldId);
      const participantOrder = participantsIds.indexOf(fieldParticipantId) + 1;

      const positionsValue = positions[fieldId];
      const sizesValue = sizes[fieldId];

      return {
        ...currentFieldInformation,
        ...positionsValue,
        ...sizesValue,
        fieldId,
        participantFieldIndex,
        participantOrder,
      };
    });
  }
);

export const getPlaceholderParticipantOrder = createSelector(
  [participantIdsSelector, targetSelector],
  (participantIds: string[], target) => {
    if (!target) {
      return 0;
    }

    return participantIds.indexOf(target.participantId) + 1;
  }
);

export const getParticipantOrder = createSelector(
  [participantIdsSelector, getPropsParticipantId],
  (participantsIds: string[], participantId) => {
    return participantsIds.indexOf(participantId) + 1;
  }
);

export const getParticipantFieldCounts = createSelector(
  [fieldIdsSelector, informationSelector, targetSelector],
  (fieldIds, information, target) => {
    if (!target) {
      return 0;
    }

    const { participantId } = target;
    const fieldType = target.type;
    const participantInformation = Object.values(information)
      .filter((v) => v.participantId === participantId)
      .filter((v) => v.type === fieldType)
      .filter((v) => fieldIds.includes(v.id));

    return participantInformation ? participantInformation.length : 0;
  }
);

export const getParticipantIdsCount = createSelector(
  participantIdsSelector,
  (ids: string[]) => ids.length
);

export const getLatestFieldData = createSelector(
  [latestIdsSelector, targetSelector, sizesSelector, informationSelector],
  (latestIds, target, sizes, information) => {
    if (!target) {
      return null;
    }

    const { type } = target;

    if (type === FieldType.SIGNATURE_FIELD) return null;

    let latestFieldID = null;
    if (type === FieldType.CHECKBOX_GROUP) {
      latestFieldID = latestIds.checkboxGroup;
    } else if (type === FieldType.TEXT_FIELD) {
      latestFieldID = latestIds.textField;
    }

    if (!latestFieldID) {
      return null;
    }

    const sizesValue = sizes[latestFieldID];
    const informationValue = information[latestFieldID];

    return { ...sizesValue, ...informationValue };
  }
);

/**
 * 입력중인 필드의 허용 서명 유형(AllowSignatureTypes)을 반환한다.
 */
export const getTargetAllowSignatureType = createSelector(targetSelector, (target) => {
  if (target?.type === FieldType.SIGNATURE_FIELD) {
    return target.allowSignatureTypes;
  }

  return undefined;
});

export const getTargetType = createSelector(targetSelector, (target) => {
  if (!target) {
    return null;
  }
  return target.type;
});
